// The arithmetic behind a bulk run — pure, no React, no network, so the
// static suite (work-items/test/run-bulk-search-tests.mjs) drives it.
//
// The server takes at most 500 keys per bulk call. A selection made with
// "Select all matching" on Zebra Search can be larger, so the modal sends
// it in slices and folds the answers into one result, one plan.

export const CHUNK = 500;

export function chunk(keys, size = CHUNK) {
  const out = [];
  for (let i = 0; i < keys.length; i += size) out.push(keys.slice(i, i + size));
  return out;
}

// fn(slice) per slice, one after another (never all at once — a plan of
// 5,000 items is ten heavy server calls), answers in slice order.
export async function eachSlice(keys, fn) {
  const out = [];
  for (const slice of chunk(keys)) out.push(await fn(slice));
  return out;
}

// Run fn(slice) per slice, in order; fold { requested, processed,
// alreadyThere[], failed[] }. A slice that throws fails its own keys with
// the message and the run carries on — one bad slice never hides the rest.
export async function runInChunks(keys, fn, { onProgress, reasonOf } = {}) {
  const total = { requested: 0, processed: 0, alreadyThere: [], failed: [] };
  let done = 0;
  for (const slice of chunk(keys)) {
    try {
      const r = (await fn(slice)) || {};
      total.requested += r.requested ?? slice.length;
      total.processed += r.processed || 0;
      if (Array.isArray(r.alreadyThere)) total.alreadyThere.push(...r.alreadyThere);
      if (Array.isArray(r.failed)) total.failed.push(...r.failed);
    } catch (err) {
      const reason = reasonOf ? reasonOf(err) : (err?.message || 'Request failed');
      total.requested += slice.length;
      for (const key of slice) total.failed.push({ key, reason });
    }
    done += slice.length;
    if (onProgress) onProgress(done, keys.length);
  }
  return total;
}

const groupKey = (g) => `${g.workflowName || ''}|${g.statusId || ''}`;

// Transition plans from several slices → one plan: same (workflow,
// status) groups merge, and a merged group keeps only the transitions
// every slice offered (each slice already intersected its own items).
export function mergeTransitionPlans(plans) {
  const groups = new Map();
  const unavailable = [];
  for (const p of plans) {
    for (const g of p?.groups || []) {
      const k = groupKey(g);
      if (!groups.has(k)) { groups.set(k, { ...g, keys: [...g.keys], transitions: [...(g.transitions || [])] }); continue; }
      const m = groups.get(k);
      m.keys.push(...g.keys);
      const ids = new Set((g.transitions || []).map(t => String(t.id)));
      m.transitions = m.transitions.filter(t => ids.has(String(t.id)));
    }
    unavailable.push(...(p?.unavailable || []));
  }
  const out = Array.from(groups.values());
  out.sort((a, b) => b.keys.length - a.keys.length || String(a.statusName).localeCompare(String(b.statusName)));
  return { groups: out, unavailable };
}

// The chosen transition groups as request bodies of at most CHUNK keys.
export function transitionBatches(chosen) {
  const out = [];
  for (const c of chosen) {
    for (const slice of chunk(c.keys)) out.push([{ ...c, keys: slice }]);
  }
  return out;
}

// Parent plans from several slices → one plan, plus the three counts the
// modal shows: will change, already there, can't.
export function mergeParentPlans(plans) {
  const items = [];
  let parent = null;
  let error = null;
  for (const p of plans) {
    if (p?.parent && !parent) parent = p.parent;
    if (p?.error && !error) error = p.error;
    items.push(...(p?.items || []));
  }
  return { parent, error, items, ...countParentPlan(items) };
}

export function countParentPlan(items) {
  const willChange = items.filter(i => i.ok && !i.alreadyThere).map(i => i.key);
  const alreadyThere = items.filter(i => i.ok && i.alreadyThere).map(i => i.key);
  const refused = items.filter(i => !i.ok).map(i => ({ key: i.key, reason: i.reason }));
  return { willChange, alreadyThere, refused };
}

// The ZQL behind the parent search box: a key finds itself; anything else
// is a summary match. Site-wide on purpose — a parent may live in another
// project, and the plan says per item whether that is allowed.
export function parentSearchZql(text) {
  const q = String(text || '').trim();
  if (!q) return null;
  if (/^[A-Za-z][A-Za-z0-9_]*-\d+$/.test(q)) return `key = "${q.toUpperCase()}"`;
  return `summary ~ "${q.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}" ORDER BY updated DESC`;
}

// Delete plans from several slices → one: will go to the bin, blocked by
// children left behind (with every such child once), refused, missing.
export function mergeDeletePlans(plans) {
  const out = { deletable: [], blocked: [], refused: [], missing: [] };
  for (const p of plans) {
    out.deletable.push(...(p?.deletable || []));
    out.blocked.push(...(p?.blocked || []));
    out.refused.push(...(p?.refused || []));
    out.missing.push(...(p?.missing || []));
  }
  out.childrenToAdd = [...new Set(out.blocked.flatMap(b => b.children || []))];
  return out;
}


// Grouped Edit plans from several slices → one: the same group (project,
// work type, workflow, status) merges; its transitions are those every
// slice offered; its fields and parent rule are the group's own.
export function mergeEditPlans(plans) {
  const byId = new Map();
  const unavailable = [];
  for (const p of plans) {
    for (const g of p?.groups || []) {
      if (!byId.has(g.id)) { byId.set(g.id, { ...g, keys: [...g.keys], items: [...(g.items || [])], transitions: [...(g.transitions || [])] }); continue; }
      const m = byId.get(g.id);
      m.keys.push(...g.keys);
      m.items.push(...(g.items || []));
      const ids = new Set((g.transitions || []).map(t => String(t.id)));
      m.transitions = m.transitions.filter(t => ids.has(String(t.id)));
      m.readOnly = m.readOnly || g.readOnly;
      m.parentAllowed = m.parentAllowed && g.parentAllowed;
    }
    unavailable.push(...(p?.unavailable || []));
  }
  const groups = [...byId.values()].sort((a, b) => b.keys.length - a.keys.length
    || String(a.projectKey).localeCompare(String(b.projectKey))
    || String(a.workTypeName).localeCompare(String(b.workTypeName)));
  return { groups, unavailable };
}

// What one group's card asks for, as the three writes the run makes.
//   edit: { transition, screen, fields: [{ field, value }], parent }
// → { fields: { id: apiValue } | null, parentKey: string | null | undefined, transition | null }
//   parentKey: undefined = leave the parent alone, null = take it out.
// The cards the Edit window shows: one per project + work type (Rushane,
// 2026-10-07 — status is NOT a grouping; it is chosen inside the card).
// The server's groups (project, work type, workflow, status) become the
// card's `statuses` — each keeps its own transitions, since a transition id
// only means something from one status. The card's fields, parent rule and
// screen are its type's in its project, the same for every status; items
// in a status that is read-only for this person take only a status change
// (`editableKeys` leaves them out of field, parent and project writes).
export function familiesOf(groups) {
  const byId = new Map();
  for (const g of groups || []) {
    const id = `${g.projectKey ?? ''}|${g.workTypeId ?? ''}`;
    let f = byId.get(id);
    if (!f) {
      f = {
        id, projectKey: g.projectKey ?? null, projectName: g.projectName ?? null,
        workTypeName: g.workTypeName ?? null, workTypeId: g.workTypeId ?? null, workTypeIconUrl: g.workTypeIconUrl ?? null,
        statuses: [], keys: [], items: [], editableKeys: [], fields: null,
        parentAllowed: true, validParentTypes: g.validParentTypes ?? null,
      };
      byId.set(id, f);
    }
    f.statuses.push(g);
    f.keys.push(...(g.keys || []));
    f.items.push(...(g.items || []).map(it => ({ ...it, statusName: g.statusName ?? null, statusColor: g.statusColor ?? null })));
    if (!g.readOnly) {
      f.editableKeys.push(...(g.keys || []));
      if (f.fields === null) f.fields = g.fields || [];
      f.parentAllowed = f.parentAllowed && !!g.parentAllowed;
    }
  }
  const out = [...byId.values()].map(f => ({
    ...f,
    fields: f.fields || [],
    readOnly: f.editableKeys.length === 0,
    parentAllowed: f.editableKeys.length > 0 && f.parentAllowed,
    statuses: [...f.statuses].sort((a, b) => b.keys.length - a.keys.length || String(a.statusName).localeCompare(String(b.statusName))),
  }));
  return out.sort((a, b) => b.keys.length - a.keys.length
    || String(a.projectKey).localeCompare(String(b.projectKey))
    || String(a.workTypeName).localeCompare(String(b.workTypeName)));
}

// The status changes chosen in a card, one per status: [{ status, transition }].
// None ride along with a project move (the move decides the status).
export function chosenTransitions(family, edit) {
  if (edit?.move?.target?.projectKey) return [];
  const byStatus = edit?.transitions || {};
  return (family?.statuses || []).filter(g => byStatus[g.id]).map(g => ({ status: g, transition: byStatus[g.id] }));
}

// The keys a card's changes touch: every editable item for a field, parent
// or project change, plus the items of each status that changes status.
export function touchedKeys(family, edit, writes) {
  const out = new Set();
  if (writes && (writes.fields || writes.parentKey !== undefined || writes.move)) for (const k of family.editableKeys) out.add(k);
  for (const { status } of chosenTransitions(family, edit)) for (const k of status.keys) out.add(k);
  return [...out];
}

export function groupWrites(edit, valueFor) {
  const fields = {};
  for (const row of edit?.fields || []) {
    if (!row.field || row.value === null || row.value === undefined || row.value === '') continue;
    fields[row.field.id] = valueFor(row.field, row.value);
  }
  let parentKey;
  if (edit?.parent?.mode === 'remove') parentKey = null;
  else if (edit?.parent?.mode === 'set' && edit.parent.issue?.key) parentKey = edit.parent.issue.key;
  // A move carries its own status and parent (both decided in the target
  // project), so a status transition or a separate parent write never ride
  // along with it.
  const move = edit?.move?.target?.projectKey
    ? { targetProjectKey: edit.move.target.projectKey, workTypeId: edit.move.workTypeId || edit.move.plan?.workTypeId || null, statusId: edit.move.statusId || null }
    : null;
  return {
    fields: Object.keys(fields).length ? fields : null,
    parentKey: move ? undefined : parentKey,
    move: move ? { ...move, parentKey: parentKey || null } : null,
    transition: move ? null : (edit?.transition || null),
  };
}

export function hasWrites(w) {
  return !!(w && (w.fields || w.parentKey !== undefined || w.transition || w.move));
}

// The ZQL behind the parent list: work items of the types the hierarchy
// allows as a parent, in the project the items live in (or are moving to),
// narrowed by what was typed. types null = no hierarchy → any type.
const zqlQuote = (v) => `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
export function parentCandidatesZql({ projectKey, types, text }) {
  const parts = [];
  if (projectKey) parts.push(`project = ${zqlQuote(projectKey)}`);
  if (Array.isArray(types) && types.length) parts.push(`issuetype in (${types.map(zqlQuote).join(', ')})`);
  const q = String(text || '').trim();
  if (q) {
    if (/^[A-Za-z][A-Za-z0-9_]*-\d+$/.test(q)) parts.push(`key = ${zqlQuote(q.toUpperCase())}`);
    else parts.push(`summary ~ ${zqlQuote(q)}`);
  }
  return `${parts.join(' AND ')} ORDER BY updated DESC`;
}
